// PRD 06 §3.1 helpers — which items the selected dropdown row shows, and the
// scope-summary descriptor shown by the DiseaseScopeCard badges.

import type { DiseaseFilterOption, ProItemRef } from '../types/pro-deployment.types';

/**
 * FEATURE_025 — whether an item belongs to the selected dropdown row. Every row shares ONE module set, so
 * each row shows only its own slice of it:
 *   - 전체 (ALL): every item;
 *   - 공통 (COMMON): common items;
 *   - a group row: common items + that group's group items;
 *   - a code row: common items + the code's group's group items + that code's own items.
 * Compared by id, never by group name — a name is free text and a drifted spelling silently hid rows.
 * Until the options have loaded (`option` undefined) a group or code row shows common items only, the same
 * answer the server gives for a row it cannot resolve.
 */
export function itemInView(
  item: ProItemRef,
  diseaseFilter: string,
  option: DiseaseFilterOption | undefined,
): boolean {
  if (diseaseFilter === 'ALL') return true;
  if (item.scope === 'COMMON') return true;
  if (diseaseFilter === 'COMMON' || !option) return false;
  if (item.scope === 'GROUP') {
    return option.groupId != null && item.diseaseGroupId === option.groupId;
  }
  if (item.scope === 'SPECIFIC') {
    return option.codeId != null && item.diseaseCodeId === option.codeId;
  }
  return false;
}

/**
 * FEATURE_025 (PRO Distribution Management) — the layer a deploy of the selected row publishes, or null for a row
 * that cannot be deployed: 전체, and a group or code row whose option has not loaded or names no id.
 */
export type DeployLayer =
  | { kind: 'COMMON' }
  | { kind: 'GROUP'; groupName: string }
  | { kind: 'CODE'; code: string };

export function deployLayerOf(
  diseaseFilter: string,
  option: DiseaseFilterOption | undefined,
): DeployLayer | null {
  if (diseaseFilter === 'COMMON') return { kind: 'COMMON' };
  if (!option) return null;
  if (option.codeId != null) return { kind: 'CODE', code: option.value };
  if (option.groupId != null && diseaseFilter.startsWith('GRP:')) {
    return { kind: 'GROUP', groupName: option.group ?? diseaseFilter.slice(4) };
  }
  return null;
}

/**
 * FEATURE_025 — whether an item is shown on a code row only for reference: the code row deploys its own
 * disease-specific items, so its common and group items are marked 조회 전용 (PRD 06 §3.1, §5).
 */
export function isViewOnlyOnRow(item: ProItemRef, option: DiseaseFilterOption | undefined): boolean {
  return option?.codeId != null && item.scope !== 'SPECIFIC';
}

export type ScopeSummary =
  | { kind: 'ALL' }
  | { kind: 'COMMON' }
  | { kind: 'GROUP'; group: string }
  | { kind: 'SPECIFIC'; group: string; specificLabel: string };

export function buildScopeSummary(
  diseaseFilter: string,
  options: DiseaseFilterOption[],
): ScopeSummary {
  if (diseaseFilter === 'ALL') return { kind: 'ALL' };
  if (diseaseFilter === 'COMMON') return { kind: 'COMMON' };
  if (diseaseFilter.startsWith('GRP:')) {
    return { kind: 'GROUP', group: diseaseFilter.slice(4) };
  }
  // Specific disease code (e.g. 'F32'). Use the REAL group name the BE sends on
  // the option (`group`) — never an ICD-prefix guess, which breaks for any group
  // whose name isn't a fixed Korean label (e.g. a custom 'Disease Group 00').
  const match = options.find((o) => o.value === diseaseFilter);
  return {
    kind: 'SPECIFIC',
    group: match?.group ?? '',
    specificLabel: match?.label ?? diseaseFilter,
  };
}
